import { useState, useEffect } from "react";
import { ShieldCheck, Plus, Search, Filter, MapPin, Users, Trash2, RefreshCw, Download, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { generateExport } from "@/utils/exportUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CandidateData } from "@/components/CandidateFormDialog";
import AdminCandidateFormDialog from "@/components/AdminCandidateFormDialog";
import { BulkImportDialog } from "@/components/BulkImportDialog";
import { WordImportDialog } from "@/components/WordImportDialog";
import { WebImportDialog } from "@/components/WebImportDialog";
import { useToast } from "@/hooks/use-toast";
import { moroccoRegions, allPartiesIncludingNonParliamentary } from "@/data/parties";
import { useCandidatesStore } from "@/data/candidatesStore";
import { getApiUrl } from "@/utils/apiConfig";



const STORAGE_KEY = "kaynin_admin_candidates_all";

export default function AdminCandidatesPage() {
  const [candidates, setCandidates] = useState<(CandidateData & { partyAcronym: string; source: "parti" | "admin"; publishStatus?: "draft" | "published" })[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState<(CandidateData & { partyAcronym: string }) | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [partyFilter, setPartyFilter] = useState("ALL");
  const [regionFilter, setRegionFilter] = useState("ALL");
  const [sourceFilter, setSourceFilter] = useState("ALL");
  const [listTypeFilter, setListTypeFilter] = useState("ALL");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [isWordOpen, setIsWordOpen] = useState(false);
  const [isWebOpen, setIsWebOpen] = useState(false);
  const { toast } = useToast();

  const { fetchPublicCandidates } = useCandidatesStore();

  useEffect(() => {
    const loadCandidates = async () => {
      try {
        const data = await fetchPublicCandidates();
        const mapped = data.map(c => ({
          ...c,
          source: (c.partyAcronym && c.partyAcronym !== "Indépendant") ? "parti" : "admin",
          publishStatus: "published"
        }));
        setCandidates(mapped as any);
        
        // Auto-sync local static candidates to the backend database to update dashboards
        if (mapped.length > 0) {
          handleSyncDB(mapped, false);
        }
      } catch (e) {
        console.error(e);
      }
    };
    loadCandidates();
  }, []);

  const handleSyncDB = async (candidatesToSync = candidates, showToast = true) => {
    if (!candidatesToSync || candidatesToSync.length === 0) return;
    try {
      const res = await fetch(`${getApiUrl()}/candidates.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: 'bulk_save',
          user_id: "admin_bulk",
          party_acronym: "UNKNOWN",
          candidates: candidatesToSync
        })
      });
      if (res.ok) {
        if (showToast) {
          toast({ title: "Synchronisation Réussie", description: "Base de données et tableaux de bord mis à jour avec les fichiers statiques." });
        }
      } else {
        throw new Error("API error");
      }
    } catch (e) {
      if (showToast) {
        toast({ title: "Erreur", description: "Échec de la synchronisation.", variant: "destructive" });
      }
    }
  };

  const handleSave = async (candidate: CandidateData & { partyAcronym: string }, status: "draft" | "published") => {
    try {
      const token = localStorage.getItem('kaynin_auth_token') || "";
      const userId = "admin_bulk"; // Mock admin id
      
      const newCandidate = { ...candidate, source: "admin" as const, publishStatus: "published" as const };
      
      await fetch(`${getApiUrl()}/candidates.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action: 'save', user_id: userId, party_acronym: candidate.partyAcronym, candidate: newCandidate })
      });
      
      const updated = [...candidates.filter(c => c.id !== candidate.id), newCandidate];
      setCandidates(updated as any);
      setEditingCandidate(null);
      toast({ title: "Succès", description: "Le candidat a été enregistré." });
    } catch(e) {
      toast({ title: "Erreur", description: "Échec de l'enregistrement.", variant: "destructive" });
    }
  };

  const handleBulkImport = async (data: any[]) => {
    try {
      const formattedCandidates = data.map(row => ({
        id: "c_" + Date.now() + "_" + Math.floor(Math.random()*1000),
        firstName: row.firstName || row.Prénom || row.Prenom || "",
        lastName: row.lastName || row.Nom || "",
        firstNameAr: row.firstNameAr || row.PrénomAr || "",
        lastNameAr: row.lastNameAr || row.NomAr || "",
        region: row.region || row.Région || row.Region || "",
        province: row.province || row.Province || "",
        listType: (row.listType || row["Type de liste"] || "").toLowerCase().includes("region") ? "regionale" : "locale",
        genre: row.genre || row.Genre || "",
        dateNaissance: row.dateNaissance || row["Date de naissance"] || "",
        categorieSociopro: row.categorieSociopro || row.Catégorie || "",
        bio: row.bio || row.Biographie || "",
        partyAcronym: row.parti || row.Parti || "Indépendant",
        source: "admin" as const,
        publishStatus: "published" as const
      }));

      const res = await fetch(`${getApiUrl()}/candidates.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: 'bulk_save',
          user_id: "admin_bulk",
          party_acronym: "UNKNOWN",
          candidates: formattedCandidates
        })
      });

      if (res.ok) {
        const updated = [...candidates, ...formattedCandidates];
        setCandidates(updated as any);
        toast({ title: "Import Réussi", description: `${formattedCandidates.length} candidats importés.` });
      } else {
        throw new Error("API returned error");
      }
    } catch (e) {
      console.error(e);
      toast({ title: "Erreur", description: "L'importation a échoué.", variant: "destructive" });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token') || "";
      await fetch(`${getApiUrl()}/candidates.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action: 'delete', id, user_id: "admin_bulk" })
      });
      const updated = candidates.filter(c => c.id !== id);
      setCandidates(updated);
      setDeleteId(null);
      toast({ title: "Candidat supprimé" });
    } catch(e) {
      toast({ title: "Erreur", description: "Impossible de supprimer.", variant: "destructive" });
    }
  };

  const handleReset = async () => {
    if (confirm("Êtes-vous sûr de vouloir supprimer TOUS les candidats de la base de données ?")) {
       try {
          const res = await fetch(`${getApiUrl()}/candidates.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('kaynin_auth_token')}` },
            body: JSON.stringify({ action: 'clear_all' })
          });
          if (res.ok) {
             setCandidates([]);
             toast({ title: "Succès", description: "Tous les candidats ont été supprimés." });
          }
       } catch (e) {
          console.error(e);
          toast({ title: "Erreur", description: "Impossible de réinitialiser la base.", variant: "destructive" });
       }
    }
  };

  const handleExport = () => {
    const dataToExport = filtered.map(c => ({
      "Prénom": c.firstName,
      "Nom": c.lastName,
      "Parti": c.partyAcronym,
      "Région": c.region,
      "Province": c.province,
      "Type de Liste": c.listType,
      "Genre": c.genre || "",
      "Catégorie": c.categorieSociopro || "",
      "Source": c.source,
      "Statut": c.publishStatus || "published"
    }));
    generateExport(dataToExport, `Candidats_${partyFilter}`, "Liste des Candidats", "excel");
  };

  const filtered = candidates.filter(c => {
    const search = searchTerm.toLowerCase();
    const matchSearch =
      c.firstName.toLowerCase().includes(search) ||
      c.lastName.toLowerCase().includes(search) ||
      c.partyAcronym.toLowerCase().includes(search) ||
      c.region.toLowerCase().includes(search);
    const matchParty = partyFilter === "ALL" || c.partyAcronym === partyFilter;
    const matchRegion = regionFilter === "ALL" || c.region === regionFilter;
    const matchSource = sourceFilter === "ALL" || c.source === sourceFilter;
    const matchList = listTypeFilter === "ALL" || c.listType === listTypeFilter;
    return matchSearch && matchParty && matchRegion && matchSource && matchList;
  });

  // Statistiques rapides
  const totalByParty = candidates.reduce((acc, c) => {
    acc[c.partyAcronym] = (acc[c.partyAcronym] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const topParty = Object.entries(totalByParty).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* ── En-tête ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Gestion des Candidats</h1>
          <p className="text-muted-foreground font-body mt-1">
            Tous les candidats déclarés — ajoutés par les partis ou par l'administration.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="gap-2 text-xs h-9" onClick={handleReset}>
            <RefreshCw className="w-3.5 h-3.5" /> Réinitialiser
          </Button>
          <Button variant="outline" size="sm" className="gap-2 text-xs h-9" onClick={() => handleSyncDB(candidates)}>
             <RefreshCw className="w-3.5 h-3.5 text-emerald-600" /> Synchro BDD
          </Button>
          <Button onClick={handleExport} variant="outline" size="sm" className="gap-2 text-xs h-9">
             <Download className="w-3.5 h-3.5" /> Exporter
          </Button>
          <Button onClick={() => setIsBulkOpen(true)} variant="outline" size="sm" className="gap-2 text-xs h-9">
             <Upload className="w-3.5 h-3.5" /> Import Excel
          </Button>
          <Button onClick={() => setIsWordOpen(true)} variant="outline" size="sm" className="gap-2 text-xs h-9 text-blue-600 border-blue-200 hover:bg-blue-50">
             <Upload className="w-3.5 h-3.5" /> Import Word
          </Button>
          <Button onClick={() => setIsWebOpen(true)} variant="outline" size="sm" className="gap-2 text-xs h-9 text-indigo-600 border-indigo-200 hover:bg-indigo-50">
             <Upload className="w-3.5 h-3.5" /> Import Web
          </Button>
          <Button onClick={() => { setEditingCandidate(null); setIsFormOpen(true); }} className="gap-2 h-9 px-5 rounded-xl font-bold bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4" /> Ajouter un candidat
          </Button>
        </div>
      </div>

      {/* ── KPIs rapides ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-primary shadow-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground font-semibold">Total candidats</p>
            <p className="text-2xl font-display font-bold text-primary mt-1">{candidates.length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-violet-500 shadow-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground font-semibold">Ajoutés par Admin</p>
            <p className="text-2xl font-display font-bold text-violet-600 mt-1">{candidates.filter(c => c.source === "admin").length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500 shadow-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground font-semibold">Ajoutés par Partis</p>
            <p className="text-2xl font-display font-bold text-emerald-600 mt-1">{candidates.filter(c => c.source === "parti").length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500 shadow-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground font-semibold">Parti le + actif</p>
            <p className="text-2xl font-display font-bold text-amber-600 mt-1">{topParty ? `${topParty[0]} (${topParty[1]})` : "—"}</p>
          </CardContent>
        </Card>
      </div>

      {/* ── Filtres ── */}
      <Card className="border-border/50 shadow-sm">
        <div className="p-4 bg-muted/20 border-b border-border grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Nom, parti, région..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full h-10 pl-10 pr-4 rounded-xl border border-input bg-background outline-none focus:ring-2 focus:ring-primary/20 text-sm font-body"
            />
          </div>

          <Select value={partyFilter} onValueChange={setPartyFilter}>
            <SelectTrigger className="h-10 rounded-xl bg-background"><SelectValue placeholder="Parti" /></SelectTrigger>
            <SelectContent className="max-h-[280px]">
              <SelectItem value="ALL">Tous les partis</SelectItem>
              {allPartiesIncludingNonParliamentary.map(p => (
                <SelectItem key={p.acronym} value={p.acronym}>{p.acronym}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={regionFilter} onValueChange={setRegionFilter}>
            <SelectTrigger className="h-10 rounded-xl bg-background"><SelectValue placeholder="Région" /></SelectTrigger>
            <SelectContent className="max-h-[280px]">
              <SelectItem value="ALL">Toutes les régions</SelectItem>
              {moroccoRegions.map(r => (
                <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="h-10 rounded-xl bg-background"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toutes les sources</SelectItem>
              <SelectItem value="admin">Ajouté par Admin</SelectItem>
              <SelectItem value="parti">Ajouté par Parti</SelectItem>
            </SelectContent>
          </Select>

          <Select value={listTypeFilter} onValueChange={setListTypeFilter}>
            <SelectTrigger className="h-10 rounded-xl bg-background"><SelectValue placeholder="Type de liste" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tous les types</SelectItem>
              <SelectItem value="locale">Liste Locale</SelectItem>
              <SelectItem value="regionale">Liste Régionale</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* ── Tableau principal ── */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-muted-foreground uppercase bg-muted/40 font-semibold border-b border-border">
              <tr>
                <th className="px-5 py-3">Candidat</th>
                <th className="px-5 py-3">Parti</th>
                <th className="px-5 py-3">Circonscription</th>
                <th className="px-5 py-3">Type Liste</th>
                <th className="px-5 py-3">Source</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.map(c => (
                <tr key={c.id} className="border-t border-border/50 hover:bg-muted/10 transition-colors group">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                        {c.photoUrl
                          ? <img src={c.photoUrl} alt="" className="w-full h-full object-cover" />
                          : <Users className="w-4 h-4 text-primary" />
                        }
                      </div>
                      <div>
                        <p className="font-bold text-foreground group-hover:text-primary transition-colors">{c.firstName} {c.lastName}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1 max-w-[180px]">{c.bio || "—"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                      {c.partyAcronym}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5 text-xs">
                      <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <div>
                        <p className="font-medium text-foreground">{c.province || c.region}</p>
                        <p className="text-muted-foreground">{c.region}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant="outline" className={`text-xs font-semibold ${c.listType === 'locale' ? 'border-blue-200 text-blue-700 bg-blue-50' : 'border-purple-200 text-purple-700 bg-purple-50'}`}>
                      {c.listType === 'locale' ? 'Locale' : 'Régionale'}
                    </Badge>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-col gap-1 items-start">
                      <Badge variant="outline" className={`text-xs font-semibold ${c.source === 'admin' ? 'border-violet-200 text-violet-700 bg-violet-50' : 'border-emerald-200 text-emerald-700 bg-emerald-50'}`}>
                        {c.source === 'admin' ? '👤 Admin' : '🏛️ Parti'}
                      </Badge>
                      {c.publishStatus === 'draft' && (
                         <Badge className="bg-orange-100 text-orange-800 text-[10px] font-bold border-none w-fit">BROUILLON</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {c.publishStatus === "draft" && (
                         <Button variant="outline" size="sm" className="h-8 px-2 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => {
                            const updated = candidates.map(cand => cand.id === c.id ? { ...cand, publishStatus: "published" as const } : cand);
                            setCandidates(updated);
                            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
                         }}>Publier</Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-8 h-8 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10"
                        onClick={() => { setEditingCandidate(c); setIsFormOpen(true); }}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-8 h-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteId(c.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="px-5 py-16 text-center text-muted-foreground">
                    <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
                    Aucun candidat ne correspond à ces filtres.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-border flex justify-between items-center text-xs text-muted-foreground font-body">
          <p>{filtered.length} candidat(s) affiché(s) sur {candidates.length} total</p>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 font-semibold">👤 Admin</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">🏛️ Parti</span>
          </div>
        </div>
      </Card>

      {/* ── Confirmation suppression ── */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDeleteId(null)}>
          <Card className="w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-lg font-display text-destructive flex items-center gap-2">
                <Trash2 className="w-5 h-5" /> Supprimer ce candidat ?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground font-body">Cette action est irréversible. Le candidat sera retiré de la plateforme.</p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setDeleteId(null)}>Annuler</Button>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(deleteId)}>Confirmer la suppression</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <AdminCandidateFormDialog
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingCandidate(null); }}
        onSave={handleSave}
        initialData={editingCandidate}
      />
      
      <BulkImportDialog 
        isOpen={isBulkOpen} 
        onClose={() => setIsBulkOpen(false)} 
        onImport={handleBulkImport} 
        type="candidates" 
      />
      
      <WordImportDialog
        isOpen={isWordOpen}
        onClose={() => setIsWordOpen(false)}
        onImport={handleBulkImport}
      />
      
      <WebImportDialog
        isOpen={isWebOpen}
        onClose={() => setIsWebOpen(false)}
        onImport={handleBulkImport}
      />
    </div>
  );
}

